import { dynamicIconImports } from 'lucide-react/dynamic';
import { isAgentAppointedWithCarrier } from 'helpers/appointments/appointments';
import { isCarrierDisabledByAgencyPreferences } from 'helpers/carriers/carriers';
import {
  OEP_UPDATE_ICON_COLOR,
  OEP_UPDATE_ICON_DESCRIPTION,
  OEP_UPDATE_ICON_NAME,
  shouldShowOepUpdateIcon
} from 'helpers/oepUpdates';

function isValidLucideIcon(name) {
  const iconName = typeof name === 'string' ? name.trim() : '';
  return Boolean(iconName && iconName in dynamicIconImports);
}

function getLegendKey({
  carrierIcon,
  carrierIconColor,
  carrierIconDescription
}) {
  return [
    (carrierIcon || '').trim(),
    (carrierIconColor || '').trim(),
    (carrierIconDescription || '').trim()
  ].join('|');
}

/** Visible carriers in a section → unique icon + color + description legend entries. */
export function collectSectionCarrierIconLegends(carriers, user) {
  const seen = new Set();
  const legends = [];
  let hasAnyOepUpdate = false;

  for (const carrier of carriers ?? []) {
    const isAppointed = isAgentAppointedWithCarrier(carrier.name, user);
    if (carrier.appointmentsDisabled && !isAppointed) continue;

    // flag whether any visible carrier in the section needs the shared OEP
    // legend entry appended below
    if (!hasAnyOepUpdate && shouldShowOepUpdateIcon(carrier, user)) {
      hasAnyOepUpdate = true;
    }

    const carrierIcon = (carrier.carrierIcon || '').trim();
    const carrierIconDescription = (
      carrier.carrierIconDescription || ''
    ).trim();
    if (
      !carrierIcon ||
      !carrierIconDescription ||
      !isValidLucideIcon(carrierIcon)
    ) {
      continue;
    }

    const entry = {
      carrierIcon,
      carrierIconColor: (carrier.carrierIconColor || '').trim(),
      carrierIconDescription
    };
    const key = getLegendKey(entry);
    if (seen.has(key)) continue;

    seen.add(key);
    legends.push(entry);
  }

  // append the shared OEP legend row once per section when at least one
  // carrier in the section is showing the megaphone badge on its card
  if (hasAnyOepUpdate) {
    legends.push({
      carrierIcon: OEP_UPDATE_ICON_NAME,
      carrierIconColor: OEP_UPDATE_ICON_COLOR,
      carrierIconDescription: OEP_UPDATE_ICON_DESCRIPTION
    });
  }

  return legends;
}

export const UPLINE_DISABLED_SECTION_MESSAGE = 'Disabled by Your Upline';

export function sectionHasUplineDisabledCarriers(carriers, user) {
  return (carriers ?? []).some(carrier =>
    isCarrierDisabledByAgencyPreferences(carrier, user)
  );
}

/** Carriers rendered in a parent section grid (main list + all subgroups). */
export function getCarriersInParentSection({
  carriersWithoutSubgroup = [],
  subgroups = []
}) {
  return [
    ...carriersWithoutSubgroup,
    ...(subgroups ?? []).flatMap(({ carriers }) => carriers ?? [])
  ];
}

/** Legends + upline flag for a parent section (optionally grid-filtered). */
export function getCarrierParentSectionMeta(
  sectionCarriers,
  user,
  { filterForGrid = true } = {}
) {
  const carriersForMeta = filterForGrid
    ? filterCarriersVisibleInGrid(sectionCarriers, user)
    : sectionCarriers ?? [];

  return {
    legends: collectSectionCarrierIconLegends(carriersForMeta, user),
    showUplineDisabled: sectionHasUplineDisabledCarriers(carriersForMeta, user)
  };
}

/** Match CarrierGrid visibility rules in tailwind MainSectionCarriers. */
export function filterCarriersVisibleInGrid(carriers, user) {
  return (carriers ?? []).filter(carrier => {
    if (isCarrierDisabledByAgencyPreferences(carrier, user)) return true;
    const isAppointed = isAgentAppointedWithCarrier(carrier.name, user);
    return !carrier.appointmentsDisabled || isAppointed;
  });
}
